import { TransformInsights } from 'libs/end-to-end/src/types/databases/transform/transform-insights';
import { FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionForExtras } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinition } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitionWithValues } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FILTER_SET_DEFAULT_NAME } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionDisplayType } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterDefinitionDisplayTypes } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinition } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitions } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { FilterSetDefinitionsWithDefaultFilters } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { DatabaseRevisionSchemaValidationFilterError } from 'libs/end-to-end/src/types/databases/revisions/database-revision-schema-validation-result';
import { z } from 'zod';
import { ListItem } from 'libs/end-to-end/src/types/generic/generic';
import { FocusAreaToolsDefinition } from 'libs/end-to-end/src/types/databases/focus-areas/focus-area-definition';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { FiltersStateSelection } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FilterStoreScope } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { PrimaryFilterKey } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { DimensionDefinition } from 'libs/end-to-end/src/types/databases/dimensions/dimension-definition';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { buildDependencyGraph } from 'libs/end-to-end/src/utils/databases/filter-dependency-graph';
import { determineOptionsAvailable } from 'libs/end-to-end/src/utils/databases/filter-dependency-graph';
import { sortFilterValues } from 'libs/end-to-end/src/sorting';
import { getDateAsYYYYMMDD } from 'libs/end-to-end/src/dates';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { ColumnFilterDisallowedReason } from 'libs/end-to-end/src/column-filter-disallow-reason';
import { ViewByOption } from 'libs/end-to-end/src/view-by';
import { BigTimePeriodComparison } from 'libs/end-to-end/src/big-time-period-comparison';
import { DateRangeFilter } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE } from 'libs/end-to-end/src/progress';
import { differenceInYears } from 'date-fns';
import { differenceInMonths } from 'date-fns';
import { differenceInWeeks } from 'date-fns';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { DateRange } from 'libs/end-to-end/src/date-range';
import { EntitlementFeature } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToBiasAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToBinScatterMultiSelect } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToGeolocation } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToHeatmap } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToProgress } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSuccessFactors } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToSummaryStats } from 'libs/end-to-end/src/group-entitlements';
import { isEntitledToTableView } from 'libs/end-to-end/src/group-entitlements';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { DynamicPipelineAnalysisConfiguration } from 'libs/end-to-end/src/dynamic-pipeline-analysis-configuration';
import { CustomVizId } from 'libs/end-to-end/src/custom-viz/CustomVizId';

export const GROUP_BY_DEFAULT_ITEM_KEY = 'overallAverage';

export const SELECTED_SECONDARY_BREAKDOWN_NO_SELECTION_VALUE = 'noSelection';

export interface FocusAreaWithFilterSetNames {
  focusArea: FocusArea;
  dataIntelligenceDeepDiveFilterSetName: string | null;
  binScatterMultiSelectFilterSetName: string | null;
}

type FilterConfig = {
  primaryFilterKeys: PrimaryFilterKey[];
  allowFilteringOnDimensions: boolean;
  filterSetName: (params: {
    focusArea: FocusArea | null;
    focusAreas: FocusAreaWithFilterSetNames[];
    filterSetDefinitions: FilterSetDefinitionWithValues[];
    analysisName: string | undefined;
    pipelineAnalysisConfiguration:
      | DynamicPipelineAnalysisConfiguration[]
      | undefined;
  }) => string;
  omitInvalidFilterDefinitions: (
    filterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'],
  ) => FilterSetDefinitionWithValues['filterDefinitions'];
  filterStoreScopeIncludesFocusArea: boolean;
  filterStoreScopeIncludesAnalysisName: boolean;
};

export const filterConfigMap: Record<FiltersFeatureName, FilterConfig> = {
  dataIntelligenceDeepDive: {
    primaryFilterKeys: ['employeeGroup', 'comparisonGroup'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.dataIntelligenceDeepDiveFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: true,
    filterStoreScopeIncludesAnalysisName: false,
  },
  exploreHeatmap: {
    primaryFilterKeys: ['employeeGroup', 'comparisonGroup', 'metrics'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.dataIntelligenceDeepDiveFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: true,
    filterStoreScopeIncludesAnalysisName: false,
  },
  progressPage: {
    primaryFilterKeys: ['focusArea', 'dimension', 'comparisonGroup'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.dataIntelligenceDeepDiveFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    // We don't want to show the year filter on the progress page. ATM we always display values from the current year up to T - 3 .
    omitInvalidFilterDefinitions: (filterDefinitions) =>
      filterDefinitions.filter((f) => f.columnHeaderValue !== 'year'),
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
  successFactors: {
    primaryFilterKeys: ['focusArea'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.dataIntelligenceDeepDiveFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
  employeeLifecycle: {
    primaryFilterKeys: ['dimension'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ analysisName, pipelineAnalysisConfiguration }) => {
      const config = pipelineAnalysisConfiguration?.find(
        (config) => config.name === analysisName,
      );

      if (config) {
        return config.filterSetName || FILTER_SET_DEFAULT_NAME;
      }

      return FILTER_SET_DEFAULT_NAME;
    },
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: true,
  },
  binScatterMultiSelect: {
    primaryFilterKeys: ['dimension', 'metric'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.binScatterMultiSelectFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: true,
    filterStoreScopeIncludesAnalysisName: false,
  },
  tableView: {
    primaryFilterKeys: ['groupBy', 'secondaryBreakdown', 'metrics'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.dataIntelligenceDeepDiveFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
  timeSeriesTrendsPrimary: {
    primaryFilterKeys: [
      'focusArea',
      'groupBy',
      'viewBy',
      'bigTimePeriodComparison',
      'dateRange',
      'chartType',
      'singleDate',
      'eventStudyGroup',
    ],
    allowFilteringOnDimensions: false,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.dataIntelligenceDeepDiveFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
  summaryStats: {
    primaryFilterKeys: ['metrics'],
    allowFilteringOnDimensions: true,
    filterSetName: ({ focusArea, focusAreas }) =>
      focusAreas.find((definition) => definition.focusArea === focusArea)
        ?.dataIntelligenceDeepDiveFilterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
  richText: {
    primaryFilterKeys: [],
    allowFilteringOnDimensions: false,
    filterSetName: () => FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: () => [],
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
  externalStreamlit: {
    primaryFilterKeys: [],
    allowFilteringOnDimensions: false,
    filterSetName: () => FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: () => [],
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
  geolocation: {
    primaryFilterKeys: ['metrics', 'dateRange'],
    allowFilteringOnDimensions: false,
    filterSetName: ({ filterSetDefinitions }) =>
      filterSetDefinitions.find(
        (definition) => definition.filterSetName === 'geolocation',
      )?.filterSetName ?? FILTER_SET_DEFAULT_NAME,
    omitInvalidFilterDefinitions: (filterDefinitions) => filterDefinitions,
    filterStoreScopeIncludesFocusArea: false,
    filterStoreScopeIncludesAnalysisName: false,
  },
};

export const FiltersFeatureName = z.enum([
  'dataIntelligenceDeepDive',
  'progressPage',
  'exploreHeatmap',
  'successFactors',
  'employeeLifecycle',
  'binScatterMultiSelect',
  'tableView',
  'timeSeriesTrendsPrimary',
  'summaryStats',
  'richText',
  'geolocation',
  'externalStreamlit',
]);

export type FiltersFeatureName = z.infer<typeof FiltersFeatureName>;

export const ToolsDefinitionValidationError = z
  .enum(['invalidDataIntelligenceDeepDiveFilterSetName'])
  .describe('ToolsDefinitionValidationError');

export type ToolsDefinitionValidationError = z.infer<
  typeof ToolsDefinitionValidationError
>;

export const ColumnFilterAllowance = z.object({
  allowedDisplayTypes: z.array(FilterDefinitionDisplayType),
  disallowedDisplayTypes: z.array(
    z.object({
      displayType: FilterDefinitionDisplayType,
      reason: ColumnFilterDisallowedReason,
    }),
  ),
});

export type ColumnFilterAllowance = z.infer<typeof ColumnFilterAllowance>;

/**
 * Checks if a column is allowed to be a filter, based on information gathered in the transform insights.
 *
 * A column may be filtered upon if it either has unique values or is a numeric or date column.
 *
 * @param column
 * @param numericColumns (get this from the transform insights)
 * @param dateColumns (get this from the transform insights)
 * @param uniqueValuesByColumn (get this from the transform insights)
 */
export const checkIfColumnIsAllowedToBeFilter = ({
  column,
  numericColumns,
  dateColumns,
  uniqueValuesByColumn,
}: {
  column: string;
  uniqueValuesByColumn: TransformInsights['uniqueValuesByColumn'];
  numericColumns: TransformInsights['numericColumns'];
  dateColumns: TransformInsights['dateColumns'];
}): ColumnFilterAllowance => {
  const hasUniqueValues = uniqueValuesByColumn.some(
    (item) => item.columnName === column && item.uniqueValues.length > 0,
  );

  const numericColumnInfo = numericColumns?.[column];
  const dateColumnInfo = dateColumns?.[column];

  const disallowedDisplayTypes: ColumnFilterAllowance['disallowedDisplayTypes'] =
    [];

  if (!hasUniqueValues) {
    disallowedDisplayTypes.push(
      {
        displayType: 'select_single',
        reason: 'unique-values-required',
      },
      {
        displayType: 'select_multi',
        reason: 'unique-values-required',
      },
    );
  }

  if (numericColumnInfo === undefined) {
    disallowedDisplayTypes.push({
      displayType: 'range_scalar',
      reason: 'not-number',
    });
  } else if (numericColumnInfo.anyBlanks) {
    disallowedDisplayTypes.push({
      displayType: 'range_scalar',
      reason: 'has-blanks',
    });
  }

  if (dateColumnInfo === undefined) {
    disallowedDisplayTypes.push({
      displayType: 'range_date',
      reason: 'not-date',
    });
  } else if (dateColumnInfo.anyBlanks) {
    disallowedDisplayTypes.push({
      displayType: 'range_date',
      reason: 'has-blanks',
    });
  }

  const allowedDisplayTypes = FilterDefinitionDisplayTypes.filter(
    (displayType) =>
      !disallowedDisplayTypes.some(
        (disallowed) => disallowed.displayType === displayType,
      ),
  );

  return {
    allowedDisplayTypes,
    disallowedDisplayTypes,
  };
};

/**
 * Applies changes to the values of a filter.
 *
 * The result will be the selections with the added selections added and the removed selections removed.
 * No errors will occur if a value is removed that is not in the selections.
 *
 * @param addedSelections
 * @param removedSelections
 * @param selections
 */
export const applyChangesToValues = ({
  addedSelections,
  removedSelections,
  selections,
}: {
  selections: string[];
  addedSelections: string[];
  removedSelections: string[];
}): string[] => {
  const newValues = [...selections];

  for (const value of addedSelections) {
    if (!newValues.includes(value)) {
      newValues.push(value);
    }
  }

  for (const value of removedSelections) {
    if (newValues.includes(value)) {
      newValues.splice(newValues.indexOf(value), 1);
    }
  }

  return newValues;
};

export const validateFilterSets = ({
  filterSets,
  numericColumns,
  uniqueValuesByColumn,
  dateColumns,
}: {
  filterSets: FilterSetDefinitions;
  uniqueValuesByColumn: TransformInsights['uniqueValuesByColumn'];
  numericColumns: TransformInsights['numericColumns'];
  dateColumns: TransformInsights['dateColumns'];
}): DatabaseRevisionSchemaValidationFilterError[] => {
  const errors: DatabaseRevisionSchemaValidationFilterError[] = [];

  for (const set of filterSets) {
    if (
      !isValidFilterSetName({ filterSetName: set.filterSetName, filterSets })
    ) {
      errors.push({
        filterSetName: set.filterSetName,
        details: {
          reason: 'invalid-filter-set-name',
        },
      });
    }

    for (const filter of set.filterDefinitions) {
      const filterDisplayType =
        filter.displayType ?? FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE;

      const allowance = checkIfColumnIsAllowedToBeFilter({
        column: filter.columnHeaderValue,
        dateColumns,
        numericColumns,
        uniqueValuesByColumn,
      });

      const disallowedReason =
        allowance.disallowedDisplayTypes?.find(
          (disallowed) => disallowed.displayType === filterDisplayType,
        )?.reason ?? null;

      if (disallowedReason) {
        errors.push({
          filterSetName: set.filterSetName,
          details: {
            columnHeaderValue: filter.columnHeaderValue,
            reason: disallowedReason,
          },
        });
      }
    }
  }

  return errors;
};

/**
 * In the admin panel, this function produces the list for the filter set selection dropdown.
 * @param currentFilterSetName
 * @param filters
 */
export const getListItemsForAllFilterSets = ({
  filterSets,
  currentFilterSetName,
}: {
  filterSets: FilterSetDefinitions;
  currentFilterSetName: string | null;
}): ListItem<string>[] => {
  const currentFilterSetNameIsInvalid =
    currentFilterSetName !== null &&
    !resolveFilterSet({
      filterSets,
      filterSetName: currentFilterSetName,
    });

  const dropdownFilterSetNames = [
    ...(currentFilterSetNameIsInvalid ? [currentFilterSetName] : []),
    ...filterSets.map((f) => f.filterSetName),
  ];

  return dropdownFilterSetNames.map((filterSetName) => {
    return {
      optionLabel: filterSetName,
      optionValue: filterSetName,
    };
  });
};

export const getAllFilterSetsAsFilterSetDefinitions = (
  filters: FilterSetDefinitionsWithDefaultFilters,
): FilterSetDefinitions => {
  const defaultItem: FilterSetDefinition = {
    filterSetName: FILTER_SET_DEFAULT_NAME,
    filterDefinitions: filters.defaultFilters,
  };

  return [defaultItem, ...filters.filterSets];
};

export const getFiltersDefinitionsWithValues = ({
  filterSets,
  uniqueValuesByColumn,
}: {
  filterSets: FilterSetDefinitions;
  uniqueValuesByColumn: TransformInsights['uniqueValuesByColumn'];
}): FilterSetDefinitionWithValues[] => {
  const result: FilterSetDefinitionWithValues[] = [];

  for (const filterSet of filterSets) {
    const filterDefinitionsWithValues: FilterDefinitionWithValues[] =
      filterSet.filterDefinitions.map((filter) => {
        const uniqueValues = uniqueValuesByColumn.find(
          (uniqueValues) =>
            uniqueValues.columnName === filter.columnHeaderValue,
        )?.uniqueValues;

        return {
          ...filter,
          uniqueValues: uniqueValues ?? [],
        };
      });

    result.push({
      filterSetName: filterSet.filterSetName,
      filterDefinitions: filterDefinitionsWithValues,
    });
  }

  return result;
};

export const getPossibleFilterColumns = ({
  uniqueValuesByColumn,
  numericColumns,
  dateColumns,
}: {
  uniqueValuesByColumn: TransformInsights['uniqueValuesByColumn'];
  numericColumns: TransformInsights['numericColumns'];
  dateColumns: TransformInsights['dateColumns'];
}): string[] => {
  const allColumns = new Set([
    ...uniqueValuesByColumn.map((item) => item.columnName),
    ...Object.keys(numericColumns ?? {}),
    ...Object.keys(dateColumns ?? {}),
  ]);

  return Array.from(allColumns).filter((column) => {
    const allowance = checkIfColumnIsAllowedToBeFilter({
      column,
      uniqueValuesByColumn,
      numericColumns,
      dateColumns,
    });

    return allowance.allowedDisplayTypes.length > 0;
  });
};

export const getDefaultFilterSetFilters = (
  filterSets: FilterSetDefinitions,
): FilterDefinition[] => {
  const defaultFilterSet = resolveFilterSet({
    filterSets,
    filterSetName: FILTER_SET_DEFAULT_NAME,
  });

  return defaultFilterSet?.filterDefinitions ?? [];
};

export const getCustomFilterSets = (
  filterSets: FilterSetDefinitions,
): FilterSetDefinition[] => {
  return filterSets.filter(
    (filterSet) => filterSet.filterSetName !== FILTER_SET_DEFAULT_NAME,
  );
};

/**
 * Any place that uses a filter set name should validate that the filter set name is valid.
 *
 * @param filterSetName - must be alphanumeric, underscores, or hyphens (no spaces, special characters, etc.)
 * @param filters
 */
export const isValidFilterSetName = ({
  filterSetName,
  filterSets,
}: {
  filterSetName: string;
  filterSets: FilterSetDefinitions;
}) =>
  Boolean(filterSetName.match(/^[a-zA-Z0-9_\-]+$/)) &&
  Boolean(resolveFilterSet({ filterSetName, filterSets }));

export const resolveFilterSet = ({
  filterSets,
  filterSetName,
}: {
  filterSets: FilterSetDefinitions;
  filterSetName: string | undefined;
}): FilterSetDefinition | null => {
  return (
    filterSets.find((filterSet) => filterSet.filterSetName === filterSetName) ??
    null
  );
};

export const getToolsDefinitionValidationErrors = ({
  toolsDefinition,
  filterSets,
  dataIntelligenceDeepDiveIsEnabled,
}: {
  toolsDefinition: FocusAreaToolsDefinition;
  filterSets: FilterSetDefinitions;
  dataIntelligenceDeepDiveIsEnabled: boolean;
}) => {
  const validationErrors: ToolsDefinitionValidationError[] = [];
  const dataIntelligenceDeepDiveFilterSet = resolveFilterSet({
    filterSets,
    filterSetName:
      toolsDefinition.dataIntelligenceDeepDiveDefinition?.filterSetName ??
      FILTER_SET_DEFAULT_NAME,
  });

  if (dataIntelligenceDeepDiveIsEnabled && !dataIntelligenceDeepDiveFilterSet) {
    validationErrors.push('invalidDataIntelligenceDeepDiveFilterSetName');
  }

  return validationErrors;
};

export const determinePrimaryFilterKeysForFeature = ({
  feature,
}: {
  feature: FiltersFeatureName;
}): PrimaryFilterKey[] => {
  return filterConfigMap[feature].primaryFilterKeys;
};

export const determineSecondaryFilterDefinitionsForFeature = ({
  feature,
  focusArea,
  filterSetDefinitionsWithValues,
  focusAreas,
  analysisName,
  dimensionDefinitions,
  pipelineAnalysisConfiguration,
}: {
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  focusAreas: FocusAreaWithFilterSetNames[];
  analysisName: string | undefined;
  filterSetDefinitionsWithValues: FilterSetDefinitionWithValues[];
  dimensionDefinitions: DimensionDefinition[];
  pipelineAnalysisConfiguration:
    | DynamicPipelineAnalysisConfiguration[]
    | undefined;
}): FilterDefinitionWithValues[] => {
  const filterSetName = determineFilterSetName({
    feature,
    focusAreas,
    focusArea,
    filterSetDefinitionsWithValues,
    analysisName,
    pipelineAnalysisConfiguration,
  });

  const filterDefinitions =
    filterSetDefinitionsWithValues.find(
      (definition) => definition.filterSetName === filterSetName,
    )?.filterDefinitions ?? [];

  const dynamicFilters =
    filterConfigMap[feature].omitInvalidFilterDefinitions(filterDefinitions);

  if (!filterConfigMap[feature].allowFilteringOnDimensions) {
    return dynamicFilters;
  }

  const dimensionFilterDefinitions: FilterDefinitionWithValues[] =
    dimensionDefinitions.map((dimension) => {
      // The reason we use display values here is to make them work with filters.
      const uniqueValues =
        dimension.levels.map((level) => level.columnValue) ?? [];
      const sortedUniqueValues = sortFilterValues(uniqueValues);

      // This bit of code is to create a mapping of columnValue to displayName
      const displayValues: Record<string, string> = {};
      dimension.levels.forEach((level) => {
        displayValues[level.columnValue] = level.displayName;
      });

      return {
        columnHeaderValue: dimension.columnHeaderValue,
        displayName: dimension.displayName,
        uniqueValues: sortedUniqueValues,
        optionalDisplayValues: displayValues,
      };
    });
  return [...dimensionFilterDefinitions, ...dynamicFilters];
};

export const determineGlobalFilterDefinitions = ({
  filterSetDefinitionsWithValues,
}: {
  filterSetDefinitionsWithValues: FilterSetDefinitionWithValues[];
}): FilterDefinitionWithValues[] => {
  return (
    filterSetDefinitionsWithValues
      .find((d) => d.filterSetName === FILTER_SET_DEFAULT_NAME)
      ?.filterDefinitions.filter(
        (f) => !f.displayType || f.displayType === 'select_multi',
      ) ?? []
  );
};

export const determineGlobalFilterSelections = ({
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  myObservation,
  scopeId,
}: {
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  myObservation: MyObservation | null;
  scopeId: string;
}): SecondaryFilterSelection[] => {
  const globalFilterDefinitions = determineGlobalFilterDefinitions({
    filterSetDefinitionsWithValues:
      myObservation?.filterSetDefinitionsWithValues ?? [],
  });

  return globalFilterDefinitions
    .map((def) => {
      const currentValue = getSelectedGlobalFilterValue({
        filterKey: def.columnHeaderValue,
        store,
        organizationId,
        databaseId,
        databaseRevisionId,
        scopeId,
      });

      if (currentValue === null) {
        return null;
      }

      return {
        secondaryFilterKey: def.columnHeaderValue,
        value: currentValue,
      };
    })
    .filter((s) => s !== null) as SecondaryFilterSelection[];
};

export const getPrimaryFilterFullKey = ({
  primaryFilterKey,
}: {
  primaryFilterKey: PrimaryFilterKey;
}) => {
  return `primary:${primaryFilterKey}`;
};

export const getSecondaryFilterFullKey = ({
  secondaryFilterKey,
}: {
  secondaryFilterKey: string;
}) => {
  return `secondary:${secondaryFilterKey}`;
};

export const getGlobalFilterFullKey = ({
  filterKey,
}: {
  filterKey: string;
}) => {
  return `global:${filterKey}`;
};

export interface PrimaryFilterKeyDetails {
  type: 'primary';
  key: PrimaryFilterKey;
}

export interface SecondaryFilterKeyDetails {
  type: 'secondary';
  key: string;
}

export type FilterKeyDetails =
  | PrimaryFilterKeyDetails
  | SecondaryFilterKeyDetails;

export const extractFilterKeyFromFullKey = ({
  fullKey,
}: {
  fullKey: string;
}): FilterKeyDetails => {
  if (fullKey.startsWith('primary:')) {
    return {
      type: 'primary',
      key: fullKey.replace('primary:', '') as PrimaryFilterKey,
    };
  }

  return {
    type: 'secondary',
    key: fullKey.replace('secondary:', ''),
  };
};

export const determineFilterSetName = ({
  focusArea,
  feature,
  focusAreas,
  analysisName,
  filterSetDefinitionsWithValues,
  pipelineAnalysisConfiguration,
}: {
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  focusAreas: FocusAreaWithFilterSetNames[];
  analysisName: string | undefined;
  filterSetDefinitionsWithValues: FilterSetDefinitionWithValues[];
  pipelineAnalysisConfiguration:
    | DynamicPipelineAnalysisConfiguration[]
    | undefined;
}): string => {
  return filterConfigMap[feature].filterSetName({
    focusArea,
    focusAreas,
    filterSetDefinitions: filterSetDefinitionsWithValues,
    analysisName,
    pipelineAnalysisConfiguration,
  });
};

export const determineFilterStoreScope = ({
  fullKey,
  feature,
  focusArea,
  organizationId,
  databaseRevisionId,
  databaseId,
  analysisName,
}: {
  fullKey: string;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  databaseId: string;
  databaseRevisionId: string;
  organizationId: string;
  analysisName: string | undefined;
}): FilterStoreScope | null => {
  // if fullKey is for focusArea and the feature is progressPage or the user in
  // on the dashboard page, we want to remove the selected
  // focusArea from the scope name so we can keep when it changes and reset
  // other filters
  const focusAreaForScope = fullKey === 'primary:focusArea' ? null : focusArea;

  if (filterConfigMap[feature].filterStoreScopeIncludesFocusArea) {
    return {
      databaseId,
      databaseRevisionId,
      organizationId,
      scopeName: `${feature}/${focusAreaForScope}`,
    };
  }

  if (filterConfigMap[feature].filterStoreScopeIncludesAnalysisName) {
    return {
      databaseId,
      databaseRevisionId,
      organizationId,
      scopeName: analysisName ? `${feature}/${analysisName}` : feature,
    };
  }

  return {
    databaseId,
    databaseRevisionId,
    organizationId,
    scopeName: feature,
  };
};

export const determineGlobalFilterStoreScope = ({
  organizationId,
  databaseRevisionId,
  databaseId,
  scopeId,
}: {
  databaseId: string;
  databaseRevisionId: string;
  organizationId: string;
  scopeId: string;
}): FilterStoreScope | null => {
  return {
    databaseId,
    databaseRevisionId,
    organizationId,
    scopeName: `global/${scopeId}`,
  };
};

export const determineDefaultValueForPrimaryFilter = ({
  feature,
  primaryFilterKey,
  allDimensions,
  getAllLevelsForDimension,
  entitledFilterFeatureDetails,
  myObservation,
}: {
  feature: FiltersFeatureName;
  primaryFilterKey: PrimaryFilterKey;
  allDimensions: DimensionDefinition[];
  getAllLevelsForDimension: (columnHeaderValue: string) => LevelDefinition[];
  focusArea: FocusArea | null;
  entitledFilterFeatureDetails: EntitledDetailsInContextOfFiltersFeatureName;
  myObservation: MyObservation | null;
}): FilterStoreValue | null => {
  const allLevelsForDimension =
    allDimensions.length > 0
      ? getAllLevelsForDimension(allDimensions[0].columnHeaderValue)
      : [];

  const nonPrivLevels = allLevelsForDimension.filter(
    (level) => !level.isPrivileged,
  );
  const privLevel = allLevelsForDimension.find((level) => level.isPrivileged);

  const levelToCompare = [
    'dataIntelligenceDeepDive',
    'progressPage',
    'exploreHeatmap',
    'employeeLifecycle',
  ].includes(feature)
    ? privLevel
    : allLevelsForDimension[0];

  const pipelineAnalysisConfiguration =
    myObservation?.pipelineAnalysisConfiguration ?? [];

  if (
    primaryFilterKey === 'employeeGroup' &&
    allDimensions.length > 0 &&
    nonPrivLevels.length > 0
  ) {
    return [allDimensions[0].columnHeaderValue, nonPrivLevels[0].columnValue];
  }

  if (primaryFilterKey === 'comparisonGroup' && levelToCompare) {
    return levelToCompare.columnValue;
  }

  const myObservationMetrics = myObservation?.byMetricTrendsConfiguration
    ?.isEnabled
    ? myObservation.byMetricTrendsConfiguration.metrics
    : [];

  const firstEntitledMetricForProgress = myObservationMetrics.find((metric) => {
    const focusAreaOfMetric = myObservation?.focusAreas.find((fa) =>
      fa.metrics.some((m) => m.columnHeaderValue === metric.metric),
    )?.focusArea;

    if (!focusAreaOfMetric) {
      return false;
    }

    return isEntitledToProgress({
      focusArea: focusAreaOfMetric,
      features: myObservation?.features ?? [],
    });
  })?.metric;

  if (
    primaryFilterKey === 'focusArea' &&
    feature === 'timeSeriesTrendsPrimary' &&
    firstEntitledMetricForProgress
  ) {
    const focusAreaOfMetric = myObservation?.focusAreas.find((fa) =>
      fa.metrics.some(
        (m) => m.columnHeaderValue === firstEntitledMetricForProgress,
      ),
    )?.focusArea;
    if (focusAreaOfMetric) {
      return [focusAreaOfMetric, firstEntitledMetricForProgress];
    }
  }

  if (
    primaryFilterKey === 'focusArea' &&
    entitledFilterFeatureDetails.focusAreaDefinition
  ) {
    return [
      entitledFilterFeatureDetails.focusAreaDefinition.focusArea,
      entitledFilterFeatureDetails.focusAreaDefinition.metrics[0]
        .columnHeaderValue,
    ];
  }
  if (
    primaryFilterKey === 'focusArea' &&
    entitledFilterFeatureDetails.focusAreasWithMetrics.length > 0
  ) {
    return [
      entitledFilterFeatureDetails.focusAreasWithMetrics[0].focusArea,
      entitledFilterFeatureDetails.focusAreasWithMetrics[0].metrics[0]
        .columnHeaderValue,
    ];
  }

  if (primaryFilterKey === 'dimension' && allDimensions.length > 0) {
    return allDimensions[0].columnHeaderValue;
  }

  if (
    primaryFilterKey === 'metric' &&
    entitledFilterFeatureDetails.focusAreaMetrics.length > 0
  ) {
    return entitledFilterFeatureDetails.focusAreaMetrics[0].columnHeaderValue;
  }

  if (
    primaryFilterKey === 'pipelineAnalysisConfiguration' &&
    pipelineAnalysisConfiguration.length > 0
  ) {
    return pipelineAnalysisConfiguration[0].name;
  }

  if (
    feature === 'timeSeriesTrendsPrimary' &&
    primaryFilterKey === 'groupBy' &&
    allDimensions.length > 0
  ) {
    return GROUP_BY_DEFAULT_ITEM_KEY;
  }

  if (primaryFilterKey === 'groupBy' && allDimensions.length > 0) {
    return allDimensions[0].columnHeaderValue;
  }

  if (primaryFilterKey === 'secondaryBreakdown') {
    // Secondary breakdown is optional, but we need to initialize it with an empty string
    // so it can be updated later when user makes a selection
    return SELECTED_SECONDARY_BREAKDOWN_NO_SELECTION_VALUE;
  }

  if (primaryFilterKey === 'viewBy') {
    return 'year';
  }

  if (primaryFilterKey === 'bigTimePeriodComparison') {
    return 'none';
  }

  if (primaryFilterKey === 'chartType') {
    return 'bar';
  }

  if (primaryFilterKey === 'dateRange') {
    return {
      startDate: new Date(
        new Date().setFullYear(new Date().getFullYear() - 10),
      ),
      endDate: new Date(),
      dateRangeType: 'all',
    };
  }

  if (primaryFilterKey === 'metrics') {
    return entitledFilterFeatureDetails.focusAreasWithMetrics.flatMap((fa) =>
      fa.metrics.map((m) => m.columnHeaderValue),
    );
  }

  if (primaryFilterKey === 'singleDate') {
    return EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE;
  }

  if (primaryFilterKey === 'eventStudyGroup') {
    return [];
  }

  return null;
};

export const determineDefaultValueForSecondaryFilter = ({
  secondaryFilterKey,
  feature,
  focusArea,
  myObservation,
  analysisName,
}: {
  secondaryFilterKey: string;
  focusArea: FocusArea | null;
  myObservation: MyObservation | null;
  feature: FiltersFeatureName;
  analysisName: string | undefined;
}): FilterStoreValue | null => {
  const definitions = determineSecondaryFilterDefinitionsForFeature({
    focusArea,
    filterSetDefinitionsWithValues:
      myObservation?.filterSetDefinitionsWithValues ?? [],
    focusAreas: myObservation?.focusAreas ?? [],
    feature,
    analysisName,
    dimensionDefinitions: myObservation?.dimensions ?? [],
    pipelineAnalysisConfiguration: myObservation?.pipelineAnalysisConfiguration,
  });

  const definition = definitions.find(
    (d) => d.columnHeaderValue === secondaryFilterKey,
  );

  if (!definition) {
    return null;
  }

  const displayType =
    definition.displayType ?? FILTER_DEFINITION_DEFAULT_DISPLAY_TYPE;

  if (displayType === 'select_single' && definition.uniqueValues.length > 0) {
    return definition.uniqueValues[0];
  }

  if (displayType === 'select_multi') {
    return [...definition.uniqueValues];
  }

  if (displayType === 'range_scalar') {
    const numericColumn = myObservation?.numericColumns?.[secondaryFilterKey];

    if (numericColumn) {
      return [numericColumn.minValue, numericColumn.maxValue];
    }
  }

  return null;
};

export const getValueFromStoreByPrimaryFilterKey = ({
  store,
  primaryFilterKey,
  feature,
  focusArea,
  analysisName,
  organizationId,
  databaseRevisionId,
  databaseId,
}: {
  store: FilterStoreHook;
  primaryFilterKey: PrimaryFilterKey;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}) => {
  const fullKey = getPrimaryFilterFullKey({
    primaryFilterKey,
  });

  const scope = determineFilterStoreScope({
    feature,
    focusArea,
    analysisName,
    organizationId,
    databaseRevisionId,
    databaseId,
    fullKey,
  });

  if (
    !scope ||
    !store.isInitialized({
      scope,
      key: fullKey,
    })
  ) {
    return null;
  }

  return store.getValue({
    scope,
    key: fullKey,
  });
};

export const getSelectedMetric = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}): string | null => {
  return getSelectedStringValuePrimaryFilterFromStore({
    store,
    feature,
    focusArea,
    analysisName,
    organizationId,
    databaseRevisionId,
    databaseId,
    primaryFilterKey: 'metric',
  });
};

export const getSelectedMetrics = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}): string[] | null => {
  return getSelectedListValuePrimaryFilterFromStore({
    store,
    feature,
    organizationId,
    databaseRevisionId,
    databaseId,
    primaryFilterKey: 'metrics',
    // TODO: This is kind of hacky, we should probably pass these in like the rest of the utils.
    focusArea: null,
    analysisName: undefined,
  });
};

export const isEntitledToFiltersFeatureNameInContextOfFocusArea = ({
  feature,
  features,
  focusArea,
}: {
  feature: FiltersFeatureName;
  features: EntitlementFeature[];
  focusArea: FocusArea;
}): boolean => {
  if (feature === 'summaryStats') {
    return isEntitledToSummaryStats({
      features,
      focusArea,
    });
  } else if (feature === 'dataIntelligenceDeepDive') {
    return isEntitledToBiasAnalysis({
      features,
      focusArea,
    });
  } else if (feature === 'binScatterMultiSelect') {
    return isEntitledToBinScatterMultiSelect({
      features,
      focusArea,
    });
  } else if (feature === 'exploreHeatmap') {
    return isEntitledToHeatmap({
      features,
      focusArea,
    });
  } else if (feature === 'geolocation') {
    return isEntitledToGeolocation({
      features,
      focusArea,
    });
  } else if (feature === 'tableView') {
    return isEntitledToTableView({
      features,
      focusArea,
    });
  } else if (feature === 'progressPage') {
    return isEntitledToProgress({
      features,
      focusArea,
    });
  } else if (feature === 'successFactors') {
    return isEntitledToSuccessFactors({
      features,
      focusArea,
    });
  } else if (feature === 'timeSeriesTrendsPrimary') {
    return isEntitledToProgress({
      features,
      focusArea,
    });
  } else if (
    feature === 'employeeLifecycle' ||
    feature === 'richText' ||
    feature === 'externalStreamlit'
  ) {
    return true;
  } else {
    throw guardExhaustive(feature);
  }
};

export const getSelectedStringValuePrimaryFilterFromStore = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
  primaryFilterKey,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
  primaryFilterKey: PrimaryFilterKey;
}): string | null => {
  const storeValue = getValueFromStoreByPrimaryFilterKey({
    store,
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    organizationId,
    primaryFilterKey,
  });

  if (!storeValue) {
    return null;
  }

  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type !== 'string') {
    return null;
  }

  return valueType.string;
};

export const getSelectedDateRangeFromStore = ({
  store,
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}) => {
  const storeValue = getValueFromStoreByPrimaryFilterKey({
    store,
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    organizationId,
    primaryFilterKey: 'dateRange',
  });

  if (!storeValue) {
    return null;
  }

  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type !== 'dateRange') {
    return null;
  }

  return valueType.dateRange;
};

export const getSelectedListValuePrimaryFilterFromStore = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
  primaryFilterKey,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
  primaryFilterKey: PrimaryFilterKey;
}) => {
  const storeValue = getValueFromStoreByPrimaryFilterKey({
    store,
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    organizationId,
    primaryFilterKey,
  });

  if (!storeValue) {
    return null;
  }

  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type !== 'string[]') {
    return null;
  }

  return valueType.stringArray;
};

export const getSelectedChartTypeFromFilterStore = ({
  store,
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
}) => {
  const value = getSelectedStringValuePrimaryFilterFromStore({
    store,
    feature,
    focusArea,
    organizationId,
    databaseRevisionId,
    databaseId,
    primaryFilterKey: 'chartType',
    analysisName,
  });

  if (!value) {
    return null;
  }

  if (value === 'bar' || value === 'line') {
    return value;
  }

  return null;
};

export const getSelectedEmployeeGroup = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}) => {
  const storeValue = getValueFromStoreByPrimaryFilterKey({
    store,
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    organizationId,
    primaryFilterKey: 'employeeGroup',
  });

  if (!storeValue) {
    return null;
  }

  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type !== 'string[]') {
    return null;
  }

  return {
    dimension: valueType.stringArray[0],
    level: valueType.stringArray[1],
  };
};

export const getSelectedComparisonGroup = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}) => {
  return getSelectedStringValuePrimaryFilterFromStore({
    store,
    feature,
    focusArea,
    analysisName,
    organizationId,
    databaseRevisionId,
    databaseId,
    primaryFilterKey: 'comparisonGroup',
  });
};

export const getSelectedFocusArea = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}) => {
  const storeValue = getValueFromStoreByPrimaryFilterKey({
    store,
    databaseId,
    databaseRevisionId,
    feature,
    organizationId,
    primaryFilterKey: 'focusArea',
    // TODO: This is kind of hacky, we should probably pass these in like the rest of the utils.
    focusArea: null,
    analysisName: undefined,
  });

  if (!storeValue) {
    return null;
  }

  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type !== 'string[]') {
    return null;
  }

  return {
    focusArea: FocusArea.parse(valueType.stringArray[0]),
    metric: valueType.stringArray[1],
  };
};

export const getSelectedPipelineAnalysisConfiguration = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
}) => {
  const storeValue = getValueFromStoreByPrimaryFilterKey({
    store,
    databaseId,
    databaseRevisionId,
    feature,
    focusArea,
    analysisName,
    organizationId,
    primaryFilterKey: 'pipelineAnalysisConfiguration',
  });

  if (!storeValue) {
    return null;
  }

  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type !== 'string') {
    return null;
  }

  return valueType.string;
};

export const getSelectedDimension = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
}) => {
  return getSelectedStringValuePrimaryFilterFromStore({
    store,
    feature,
    focusArea,
    analysisName,
    organizationId,
    databaseRevisionId,
    databaseId,
    primaryFilterKey: 'dimension',
  });
};

export const getSelectedSecondaryFilterValue = ({
  store,
  databaseId,
  databaseRevisionId,
  feature,
  focusArea,
  analysisName,
  organizationId,
  secondaryFilterKey,
}: {
  secondaryFilterKey: string;
  feature: FiltersFeatureName;
  store: FilterStoreHook;
  organizationId: string;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  databaseId: string;
  databaseRevisionId: string;
}): FilterStoreValue | null => {
  const fullKey = getSecondaryFilterFullKey({
    secondaryFilterKey,
  });

  const scope = determineFilterStoreScope({
    feature,
    fullKey,
    databaseId,
    databaseRevisionId,
    organizationId,
    focusArea,
    analysisName,
  });

  if (!scope || !store.isInitialized({ scope, key: fullKey })) {
    return null;
  }

  return store.getValue({
    scope,
    key: fullKey,
  });
};

export const getSelectedGlobalFilterValue = ({
  store,
  databaseId,
  databaseRevisionId,
  organizationId,
  filterKey,
  scopeId,
}: {
  filterKey: string;
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  scopeId: string;
}): FilterStoreValue | null => {
  const fullKey = getGlobalFilterFullKey({
    filterKey,
  });

  const scope = determineGlobalFilterStoreScope({
    databaseId,
    databaseRevisionId,
    organizationId,
    scopeId,
  });

  if (!scope || !store.isInitialized({ scope, key: fullKey })) {
    return null;
  }

  return store.getValue({
    scope,
    key: fullKey,
  });
};

export const isFilterStoreValueEqual = (
  a: FilterStoreValue,
  b: FilterStoreValue,
): boolean => {
  const aType = inferTypeForFilterStore(a);
  const bType = inferTypeForFilterStore(b);

  if (aType.type !== bType.type) {
    return false;
  }

  if (aType.type === 'string' && bType.type === 'string') {
    return aType.string === bType.string;
  }

  if (aType.type === 'string[]' && bType.type === 'string[]') {
    return (
      aType.stringArray.length === bType.stringArray.length &&
      aType.stringArray.every(
        (value) => bType.stringArray.indexOf(value) !== -1,
      )
    );
  }

  if (aType.type === '[number, number]' && bType.type === '[number, number]') {
    return (
      aType.numberRange[0] === bType.numberRange[0] &&
      aType.numberRange[1] === bType.numberRange[1]
    );
  }

  if (aType.type === 'date' && bType.type === 'date') {
    return aType.date.getTime() === bType.date.getTime();
  }

  if (aType.type === 'dateRange' && bType.type === 'dateRange') {
    return (
      aType.dateRange.startDate.getTime() ===
        bType.dateRange.startDate.getTime() &&
      aType.dateRange.endDate.getTime() === bType.dateRange.endDate.getTime()
    );
  }

  return false;
};

export const doesFilterStoreValueContainItem = (
  storeValue: FilterStoreValue,
  item: string,
): boolean => {
  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type === 'string[]') {
    return valueType.stringArray.includes(item);
  }

  return false;
};

export interface PrimaryFilterSelection {
  primaryFilterKey: PrimaryFilterKey;
  value: FilterStoreValue;
}

export interface SecondaryFilterSelection {
  secondaryFilterKey: string;
  value: FilterStoreValue;
}

export const determineSecondaryFilterSelections = ({
  feature,
  organizationId,
  focusArea,
  databaseRevisionId,
  databaseId,
  store,
  myObservation,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  myObservation: MyObservation | null;
  analysisName: string | undefined;
}): SecondaryFilterSelection[] => {
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      focusArea,
      feature,
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
      focusAreas: myObservation?.focusAreas ?? [],
      analysisName,
      dimensionDefinitions: myObservation?.dimensions ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

  return secondaryFilterDefinitions
    .map((def) => {
      const currentValue = getSelectedSecondaryFilterValue({
        secondaryFilterKey: def.columnHeaderValue,
        store,
        feature,
        focusArea,
        analysisName,
        organizationId,
        databaseId,
        databaseRevisionId,
      });

      if (currentValue === null) {
        return null;
      }

      return {
        secondaryFilterKey: def.columnHeaderValue,
        value: currentValue,
      };
    })
    .filter((s) => s !== null) as SecondaryFilterSelection[];
};

const convertSecondaryFilterSelectionsToSimplifiedMap = (
  stagingSecondaryFilters: SecondaryFilterSelection[],
) => {
  const inputs: Record<string, string[]> = {};

  for (const filter of stagingSecondaryFilters) {
    const type = inferTypeForFilterStore(filter.value);

    if (type.type === 'string[]') {
      inputs[filter.secondaryFilterKey] = type.stringArray;
    } else if (type.type === 'string') {
      inputs[filter.secondaryFilterKey] = [type.string];
    } else {
      inputs[filter.secondaryFilterKey] = [];
    }
  }
  return inputs;
};

export const determinePossibleSecondaryFilterValues = ({
  secondaryFilterKey,
  myObservation,
  focusArea,
  feature,
  stagingSecondaryFilters,
  analysisName,
  dashboardGlobalFilters,
}: {
  secondaryFilterKey: string;
  focusArea: FocusArea | null;
  myObservation: MyObservation | null;
  feature: FiltersFeatureName;
  stagingSecondaryFilters: SecondaryFilterSelection[];
  analysisName: string | undefined;
  dashboardGlobalFilters: FilterDefinitionForExtrasArray;
}): string[] => {
  const filterSetDefinitionsWithValues =
    myObservation?.filterSetDefinitionsWithValues ?? [];
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      focusArea,
      filterSetDefinitionsWithValues,
      focusAreas: myObservation?.focusAreas ?? [],
      feature,
      analysisName,
      dimensionDefinitions: myObservation?.dimensions ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

  const definition = secondaryFilterDefinitions.find(
    (d) => d.columnHeaderValue === secondaryFilterKey,
  );

  if (!definition) {
    return [];
  }

  const filterSetName = determineFilterSetName({
    feature,
    focusAreas: myObservation?.focusAreas ?? [],
    focusArea,
    analysisName,
    filterSetDefinitionsWithValues,
    pipelineAnalysisConfiguration: myObservation?.pipelineAnalysisConfiguration,
  });

  const inputs = convertSecondaryFilterSelectionsToSimplifiedMap(
    stagingSecondaryFilters,
  );

  const hasGlobalFiltersApplied = dashboardGlobalFilters.length > 0;

  const availableOptions = determineOptionsAvailable({
    header: secondaryFilterKey,
    graph: buildDependencyGraph(secondaryFilterDefinitions),
    fallbackUniqueValues: definition.uniqueValues,
    inputs,
    uniqueColumnMap: hasGlobalFiltersApplied
      ? []
      : (myObservation?.uniqueValuesMapPerSet.find(
          (x) => x.filterSetName === filterSetName,
        )?.uniqueValuesMap ?? []),
  });

  return sortFilterValues(availableOptions);
};

export const determinePossibleGlobalFilterValues = ({
  filterKey,
  myObservation,
  stagingGlobalFilters,
}: {
  filterKey: string;
  myObservation: MyObservation | null;
  stagingGlobalFilters: SecondaryFilterSelection[];
}): string[] => {
  const filterSetDefinitionsWithValues =
    myObservation?.filterSetDefinitionsWithValues ?? [];
  const globalFilterDefinitions = determineGlobalFilterDefinitions({
    filterSetDefinitionsWithValues,
  });

  const definition = globalFilterDefinitions.find(
    (d) => d.columnHeaderValue === filterKey,
  );

  if (!definition) {
    return [];
  }

  const inputs =
    convertSecondaryFilterSelectionsToSimplifiedMap(stagingGlobalFilters);

  const availableOptions = determineOptionsAvailable({
    header: filterKey,
    graph: buildDependencyGraph(globalFilterDefinitions),
    fallbackUniqueValues: definition.uniqueValues,
    inputs,
    uniqueColumnMap: [],
  });

  return sortFilterValues(availableOptions);
};

const mapNewValueSecondaryFilterSelection = (
  p: SecondaryFilterSelection[],
  k: string,
  v: FilterStoreValue,
): SecondaryFilterSelection[] => {
  if (p.some((s) => s.secondaryFilterKey === k)) {
    return p.map((s) => {
      if (s.secondaryFilterKey === k) {
        return {
          secondaryFilterKey: k,
          value: v,
        };
      }

      return s;
    });
  } else {
    return [
      ...p,
      {
        secondaryFilterKey: k,
        value: v,
      },
    ];
  }
};

export const determineCleanedSecondaryFiltersGivenUpdatedFilterValue = ({
  prev,
  value,
  secondaryFilterKey,
  feature,
  focusArea,
  myObservation,
  analysisName,
  dashboardGlobalFilters,
}: {
  prev: SecondaryFilterSelection[];
  value: FilterStoreValue;
  secondaryFilterKey: string;
  focusArea: FocusArea | null;
  feature: FiltersFeatureName;
  myObservation: MyObservation | null;
  analysisName: string | undefined;
  dashboardGlobalFilters: FilterDefinitionForExtrasArray;
}): SecondaryFilterSelection[] => {
  return determineCleanedSecondaryFiltersGivenUpdatedFilterValueGeneric({
    prev,
    value,
    secondaryFilterKey,
    myObservation,
    determineValuesFn: (params) => {
      return determinePossibleSecondaryFilterValues({
        secondaryFilterKey: params.secondaryFilterKey,
        focusArea,
        feature,
        myObservation,
        stagingSecondaryFilters: params.updatedSelections,
        analysisName,
        dashboardGlobalFilters,
      });
    },
  });
};

// This can work with secondary filters and global filters.
const determineCleanedSecondaryFiltersGivenUpdatedFilterValueGeneric = ({
  prev,
  value,
  secondaryFilterKey,
  determineValuesFn,
}: {
  prev: SecondaryFilterSelection[];
  value: FilterStoreValue;
  secondaryFilterKey: string;
  myObservation: MyObservation | null;
  determineValuesFn: (params: {
    secondaryFilterKey: string;
    updatedSelections: SecondaryFilterSelection[];
  }) => string[];
}) => {
  let updatedSelections = mapNewValueSecondaryFilterSelection(
    prev,
    secondaryFilterKey,
    value,
  );

  // Go through each key in the selections and if anything is now "invalid", remove it.
  let anyCleaned: boolean;

  do {
    anyCleaned = false;

    for (const selection of updatedSelections) {
      const type = inferTypeForFilterStore(selection.value);

      if (type.type !== 'string' && type.type !== 'string[]') {
        continue;
      }

      const newlyPossibleValues = determineValuesFn({
        secondaryFilterKey: selection.secondaryFilterKey,
        updatedSelections,
      });

      if (
        type.type === 'string' &&
        type.string &&
        !newlyPossibleValues.includes(type.string)
      ) {
        updatedSelections = mapNewValueSecondaryFilterSelection(
          updatedSelections,
          selection.secondaryFilterKey,
          '',
        );

        anyCleaned = true;
      } else if (
        type.type === 'string[]' &&
        type.stringArray.some((v) => !newlyPossibleValues.includes(v))
      ) {
        updatedSelections = mapNewValueSecondaryFilterSelection(
          updatedSelections,
          selection.secondaryFilterKey,
          type.stringArray.filter((v) => newlyPossibleValues.includes(v)),
        );

        anyCleaned = true;
      }
    }
  } while (anyCleaned);

  return updatedSelections;
};

export const determineCleanedGlobalFiltersGivenUpdatedFilterValue = ({
  prev,
  value,
  filterKey,
  myObservation,
}: {
  prev: SecondaryFilterSelection[];
  value: FilterStoreValue;
  filterKey: string;
  myObservation: MyObservation | null;
}): SecondaryFilterSelection[] => {
  return determineCleanedSecondaryFiltersGivenUpdatedFilterValueGeneric({
    prev,
    value,
    secondaryFilterKey: filterKey,
    myObservation,
    determineValuesFn: (params) => {
      return determinePossibleGlobalFilterValues({
        filterKey: params.secondaryFilterKey,
        myObservation,
        stagingGlobalFilters: params.updatedSelections,
      });
    },
  });
};

export type OnApplyFnArgs = {
  primaryFilterSelections: PrimaryFilterSelection[];
  secondaryFilterSelections: SecondaryFilterSelection[];
  isProgrammaticChange: boolean;
  isInitialApply: boolean;
};

export type OnApplyFn = (params: OnApplyFnArgs) => void;

export type OnApplyGlobalFnArgs = {
  globalFilterSelections: SecondaryFilterSelection[];
  isProgrammaticChange: boolean;
  isInitialApply: boolean;
};

export type OnApplyGlobalFn = (params: OnApplyGlobalFnArgs) => void;

export type OnTryInitializePrimaryFilterFn = (params: {
  primaryFilterKey: PrimaryFilterKey;
  defaultValue: FilterStoreValue;
}) => FilterStoreValue | null;

export type OnTryInitializeSecondaryFilterFn = (params: {
  secondaryFilterKey: string;
  defaultValue: FilterStoreValue;
}) => FilterStoreValue | null;

export const tryInitializeFilters = ({
  feature,
  focusArea,
  databaseId,
  databaseRevisionId,
  organizationId,
  primaryFilterKeys,
  store,
  getAllLevelsForDimension,
  allDimensions,
  entitledFilterFeatureDetails,
  myObservation,
  onTryInitializeSecondaryFilter,
  onTryInitializePrimaryFilter,
  analysisName,
  dashboardGlobalFilters,
}: {
  primaryFilterKeys: PrimaryFilterKey[];
  allDimensions: DimensionDefinition[];
  entitledFilterFeatureDetails: EntitledDetailsInContextOfFiltersFeatureName;
  getAllLevelsForDimension: (columnHeaderValue: string) => LevelDefinition[];
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  myObservation: MyObservation | null;
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  onTryInitializePrimaryFilter: OnTryInitializePrimaryFilterFn;
  onTryInitializeSecondaryFilter: OnTryInitializeSecondaryFilterFn;
  analysisName: string | undefined;
  dashboardGlobalFilters: FilterDefinitionForExtrasArray;
}) => {
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      focusArea,
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
      focusAreas: myObservation?.focusAreas ?? [],
      feature,
      analysisName,
      dimensionDefinitions: myObservation?.dimensions ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

  for (const primaryFilterKey of primaryFilterKeys) {
    const fullKey = getPrimaryFilterFullKey({ primaryFilterKey });
    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (
      !scope ||
      store.isInitialized({
        scope,
        key: fullKey,
      })
    ) {
      continue;
    }

    const initialValue = determineDefaultValueForPrimaryFilter({
      feature,
      primaryFilterKey,
      allDimensions,
      entitledFilterFeatureDetails,
      getAllLevelsForDimension,
      focusArea,
      myObservation,
    });

    if (!initialValue) {
      continue;
    }

    const finalInitial = onTryInitializePrimaryFilter({
      primaryFilterKey,
      defaultValue: initialValue,
    });

    if (finalInitial === null) {
      continue;
    }

    const inferInitial = inferTypeForFilterStore(initialValue);
    const inferFinal = inferTypeForFilterStore(finalInitial);

    let initialAs: FilterStoreValue = finalInitial;

    if (inferInitial.type !== inferFinal.type) {
      console.log(
        'ERROR: Initial and final types do not match. Falling back to initial.',
        { inferInitial, inferFinal },
      );
      initialAs = initialValue;
    }

    store.initialize({
      scope,
      key: fullKey,
      value: initialAs,
    });
  }

  let updatedSelections: SecondaryFilterSelection[] =
    determineSecondaryFilterSelections({
      feature,
      myObservation,
      focusArea,
      databaseRevisionId,
      organizationId,
      databaseId,
      store,
      analysisName,
    });

  const deferredSecondaryInitializations: {
    scope: FilterStoreScope;
    fullKey: string;
    value: FilterStoreValue;
    fallbackValue: FilterStoreValue;
  }[] = [];

  let shouldUseFallback = false;

  for (const secondaryFilterDefinition of secondaryFilterDefinitions) {
    const fullKey = getSecondaryFilterFullKey({
      secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
    });
    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (
      !scope ||
      store.isInitialized({
        scope,
        key: fullKey,
      })
    ) {
      continue;
    }

    const initialValue = determineDefaultValueForSecondaryFilter({
      secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
      myObservation,
      focusArea,
      feature,
      analysisName,
    });

    if (!initialValue) {
      continue;
    }

    const finalInitial = onTryInitializeSecondaryFilter({
      secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
      defaultValue: initialValue,
    });

    if (finalInitial === null) {
      continue;
    }

    const inferInitial = inferTypeForFilterStore(initialValue);
    const inferFinal = inferTypeForFilterStore(finalInitial);

    let initialAs: FilterStoreValue = finalInitial;

    if (inferInitial.type !== inferFinal.type) {
      console.log(
        'ERROR: Initial and final types do not match. Falling back to initial.',
        { inferInitial, inferFinal },
      );
      initialAs = initialValue;
    }

    updatedSelections = determineCleanedSecondaryFiltersGivenUpdatedFilterValue(
      {
        feature,
        focusArea,
        myObservation,
        prev: updatedSelections,
        value: initialAs,
        secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
        analysisName,
        dashboardGlobalFilters,
      },
    );

    // Check if updatedSelections.find() matches initialAs. If it doesn't, we should initial all our secondary filters as their default values, since it means one or more of the possible combinations is no longer possible.
    const updatedSelection =
      updatedSelections.find(
        (selection) =>
          selection.secondaryFilterKey ===
          secondaryFilterDefinition.columnHeaderValue,
      )?.value ?? null;

    deferredSecondaryInitializations.push({
      scope,
      fullKey,
      value: initialAs,
      fallbackValue: initialValue,
    });

    if (
      updatedSelection === null ||
      !isFilterStoreValueEqual(updatedSelection, initialAs)
    ) {
      console.log(
        'ERROR: It seems like the initial value is not possible based on the current selections. Falling back to initial.',
        {
          initialAs,
          updatedSelection,
          updatedSelections,
        },
      );

      shouldUseFallback = true;
    }
  }

  for (const {
    scope,
    fullKey,
    value,
    fallbackValue,
  } of deferredSecondaryInitializations) {
    store.initialize({
      scope,
      key: fullKey,
      value: shouldUseFallback ? fallbackValue : value,
    });
  }

  return store;
};

export const tryInitializeGlobalFilters = ({
  databaseId,
  databaseRevisionId,
  organizationId,
  store,
  onTryInitializeGlobalFilter,
  myObservation,
  scopeId,
}: {
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
  onTryInitializeGlobalFilter: OnTryInitializeSecondaryFilterFn;
  myObservation: MyObservation | null;
  scopeId: string;
}) => {
  if (!myObservation) {
    return store;
  }

  const globalFilterDefinitions = determineGlobalFilterDefinitions({
    filterSetDefinitionsWithValues:
      myObservation?.filterSetDefinitionsWithValues ?? [],
  });

  let updatedSelections: SecondaryFilterSelection[] =
    determineGlobalFilterSelections({
      myObservation,
      databaseRevisionId,
      organizationId,
      databaseId,
      store,
      scopeId,
    });

  const deferredInitializations: {
    scope: FilterStoreScope;
    fullKey: string;
    value: FilterStoreValue;
    fallbackValue: FilterStoreValue;
  }[] = [];

  let shouldUseFallback = false;

  const globalScope = determineGlobalFilterStoreScope({
    databaseId,
    databaseRevisionId,
    organizationId,
    scopeId,
  });

  for (const filterDefinition of globalFilterDefinitions) {
    const fullKey = getGlobalFilterFullKey({
      filterKey: filterDefinition.columnHeaderValue,
    });

    if (
      !globalScope ||
      store.isInitialized({
        scope: globalScope,
        key: fullKey,
      })
    ) {
      continue;
    }

    const initialValue: FilterStoreValue = [];

    const finalInitial = onTryInitializeGlobalFilter({
      secondaryFilterKey: filterDefinition.columnHeaderValue,
      defaultValue: initialValue,
    });

    if (finalInitial === null) {
      continue;
    }

    const inferInitial = inferTypeForFilterStore(initialValue);
    const inferFinal = inferTypeForFilterStore(finalInitial);

    let initialAs: FilterStoreValue = finalInitial;

    if (inferInitial.type !== inferFinal.type) {
      console.log(
        'ERROR: Initial and final types do not match. Falling back to initial.',
        { inferInitial, inferFinal },
      );
      initialAs = initialValue;
    }

    updatedSelections = determineCleanedGlobalFiltersGivenUpdatedFilterValue({
      myObservation,
      prev: updatedSelections,
      value: initialAs,
      filterKey: filterDefinition.columnHeaderValue,
    });

    // Check if updatedSelections.find() matches initialAs. If it doesn't, we should initial all our secondary filters as their default values, since it means one or more of the possible combinations is no longer possible.
    const updatedSelection =
      updatedSelections.find(
        (selection) =>
          selection.secondaryFilterKey === filterDefinition.columnHeaderValue,
      )?.value ?? null;

    deferredInitializations.push({
      scope: globalScope,
      fullKey,
      value: initialAs,
      fallbackValue: initialValue,
    });

    if (
      updatedSelection === null ||
      !isFilterStoreValueEqual(updatedSelection, initialAs)
    ) {
      console.log(
        'ERROR: It seems like the initial value is not possible based on the current selections. Falling back to initial.',
        {
          initialAs,
          updatedSelection,
          updatedSelections,
        },
      );

      shouldUseFallback = true;
    }
  }

  for (const {
    scope,
    fullKey,
    value,
    fallbackValue,
  } of deferredInitializations) {
    store.initialize({
      scope,
      key: fullKey,
      value: shouldUseFallback ? fallbackValue : value,
    });
  }

  return store;
};

export const resetSecondaryFiltersToDefault = ({
  feature,
  myObservation,
  focusArea,
  store,
  organizationId,
  databaseId,
  databaseRevisionId,
  analysisName,
}: {
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  databaseId: string;
  databaseRevisionId: string;
  organizationId: string;
  store: FilterStoreHook;
  myObservation: MyObservation | null;
  analysisName: string | undefined;
}) => {
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      focusArea,
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
      focusAreas: myObservation?.focusAreas ?? [],
      feature,
      analysisName,
      dimensionDefinitions: myObservation?.dimensions ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

  for (const secondaryFilterDefinition of secondaryFilterDefinitions) {
    const fullKey = getSecondaryFilterFullKey({
      secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
    });
    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (
      !scope ||
      !store.isInitialized({
        scope,
        key: fullKey,
      })
    ) {
      continue;
    }

    const initialValue = determineDefaultValueForSecondaryFilter({
      secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
      myObservation,
      focusArea,
      feature,
      analysisName,
    });

    if (!initialValue) {
      continue;
    }

    store.setValue({
      scope,
      key: fullKey,
      value: initialValue,
    });
  }
};

export const determineCanClearAllSecondaryFilters = ({
  feature,
  focusArea,
  databaseId,
  databaseRevisionId,
  organizationId,
  myObservation,
  store,
  analysisName,
}: {
  databaseId: string;
  databaseRevisionId: string;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  myObservation: MyObservation | null;
  organizationId: string;
  store: FilterStoreHook;
  analysisName: string | undefined;
}) => {
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      focusArea,
      focusAreas: myObservation?.focusAreas ?? [],
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
      feature,
      analysisName,
      dimensionDefinitions: myObservation?.dimensions ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

  for (const secondaryFilterDefinition of secondaryFilterDefinitions) {
    const currentValue = getSelectedSecondaryFilterValue({
      secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
      store,
      feature,
      focusArea,
      analysisName,
      databaseRevisionId,
      organizationId,
      databaseId,
    });

    if (currentValue === null) {
      continue;
    }

    const defaultValue = determineDefaultValueForSecondaryFilter({
      secondaryFilterKey: secondaryFilterDefinition.columnHeaderValue,
      myObservation,
      focusArea,
      feature,
      analysisName,
    });

    if (defaultValue === null) {
      continue;
    }

    if (!isFilterStoreValueEqual(currentValue, defaultValue)) {
      return true;
    }
  }

  return false;
};

export const setPrimaryFilters = ({
  selections,
  databaseRevisionId,
  organizationId,
  databaseId,
  feature,
  focusArea,
  analysisName,
  store,
}: {
  selections: PrimaryFilterSelection[];
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  databaseId: string;
  databaseRevisionId: string;
  organizationId: string;
  store: FilterStoreHook;
}) => {
  for (const selection of selections) {
    const fullKey = getPrimaryFilterFullKey({
      primaryFilterKey: selection.primaryFilterKey,
    });
    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (!scope) {
      return;
    }

    store.setValue({
      scope,
      key: fullKey,
      value: selection.value,
    });
  }
};

export const setSecondaryFilters = ({
  feature,
  focusArea,
  databaseRevisionId,
  organizationId,
  store,
  selections,
  databaseId,
  myObservation,
  analysisName,
  dashboardGlobalFilters,
}: {
  selections: SecondaryFilterSelection[];
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  databaseId: string;
  databaseRevisionId: string;
  organizationId: string;
  store: FilterStoreHook;
  myObservation: MyObservation | null;
  analysisName: string | undefined;
  dashboardGlobalFilters: FilterDefinitionForExtrasArray;
}) => {
  const updatedSelections = cleanSecondaryFiltersGeneric({
    selections,
    initialUpdatedSelections: determineSecondaryFilterSelections({
      feature,
      myObservation,
      focusArea,
      databaseRevisionId,
      organizationId,
      databaseId,
      store,
      analysisName,
    }),
    cleanUpdatedSelectionsFn: (params) => {
      return determineCleanedSecondaryFiltersGivenUpdatedFilterValue({
        feature,
        myObservation,
        focusArea,
        value: params.value,
        secondaryFilterKey: params.secondaryFilterKey,
        prev: params.updatedSelections,
        analysisName,
        dashboardGlobalFilters,
      });
    },
  });

  for (const updatedSelection of updatedSelections) {
    const fullKey = getSecondaryFilterFullKey({
      secondaryFilterKey: updatedSelection.secondaryFilterKey,
    });

    const scope = determineFilterStoreScope({
      feature,
      fullKey,
      databaseId,
      databaseRevisionId,
      organizationId,
      focusArea,
      analysisName,
    });

    if (!scope) {
      return;
    }

    store.setValue({
      scope,
      key: fullKey,
      value: updatedSelection.value,
    });
  }
};

const cleanSecondaryFiltersGeneric = ({
  selections,
  initialUpdatedSelections,
  cleanUpdatedSelectionsFn,
}: {
  selections: SecondaryFilterSelection[];
  initialUpdatedSelections: SecondaryFilterSelection[];
  cleanUpdatedSelectionsFn: (params: {
    value: FilterStoreValue;
    secondaryFilterKey: string;
    updatedSelections: SecondaryFilterSelection[];
  }) => SecondaryFilterSelection[];
}) => {
  let updatedSelections: SecondaryFilterSelection[] = initialUpdatedSelections;

  for (const selection of selections) {
    updatedSelections = cleanUpdatedSelectionsFn({
      value: selection.value,
      secondaryFilterKey: selection.secondaryFilterKey,
      updatedSelections,
    });
  }

  return updatedSelections;
};

export const setGlobalFilters = ({
  databaseRevisionId,
  organizationId,
  store,
  selections,
  databaseId,
  myObservation,
  scopeId,
}: {
  selections: SecondaryFilterSelection[];
  databaseId: string;
  databaseRevisionId: string;
  organizationId: string;
  store: FilterStoreHook;
  myObservation: MyObservation | null;
  scopeId: string;
}) => {
  const updatedSelections = cleanSecondaryFiltersGeneric({
    selections,
    initialUpdatedSelections: determineGlobalFilterSelections({
      myObservation,
      databaseRevisionId,
      organizationId,
      databaseId,
      store,
      scopeId,
    }),
    cleanUpdatedSelectionsFn: (params) => {
      return determineCleanedGlobalFiltersGivenUpdatedFilterValue({
        myObservation,
        value: params.value,
        filterKey: params.secondaryFilterKey,
        prev: params.updatedSelections,
      });
    },
  });

  const globalScope = determineGlobalFilterStoreScope({
    databaseId,
    databaseRevisionId,
    organizationId,
    scopeId,
  });

  if (!globalScope) {
    return;
  }

  for (const updatedSelection of updatedSelections) {
    const fullKey = getGlobalFilterFullKey({
      filterKey: updatedSelection.secondaryFilterKey,
    });

    store.setValue({
      scope: globalScope,
      key: fullKey,
      value: updatedSelection.value,
    });
  }
};

export const determineSecondaryFilterInvalidKeys = ({
  feature,
  myObservation,
  focusArea,
  stagingSecondaryFilters,
  analysisName,
}: {
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  myObservation: MyObservation | null;
  stagingSecondaryFilters: SecondaryFilterSelection[];
  analysisName: string | undefined;
}): string[] => {
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      focusArea,
      feature,
      filterSetDefinitionsWithValues:
        myObservation?.filterSetDefinitionsWithValues ?? [],
      focusAreas: myObservation?.focusAreas ?? [],
      analysisName,
      dimensionDefinitions: myObservation?.dimensions ?? [],
      pipelineAnalysisConfiguration:
        myObservation?.pipelineAnalysisConfiguration,
    });

  const invalid: string[] = [];

  for (const definition of secondaryFilterDefinitions) {
    const stagingFilter = stagingSecondaryFilters.find(
      (s) => s.secondaryFilterKey === definition.columnHeaderValue,
    );

    if (!stagingFilter) {
      invalid.push(definition.columnHeaderValue);
      continue;
    }

    const value = stagingFilter.value;

    const valueType = inferTypeForFilterStore(value);

    if (valueType.type === 'string[]' && valueType.stringArray.length === 0) {
      invalid.push(definition.columnHeaderValue);
    } else if (valueType.type === 'string' && valueType.string === '') {
      invalid.push(definition.columnHeaderValue);
    } else if (
      valueType.type === '[number, number]' &&
      valueType.numberRange[0] > valueType.numberRange[1]
    ) {
      invalid.push(definition.columnHeaderValue);
    }
  }

  return invalid;
};

export const determineSecondaryFilterDisplayName = ({
  secondaryFilterDefinitions,
  secondaryFilterKey,
}: {
  secondaryFilterKey: string;
  secondaryFilterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'];
}) => {
  const definition = secondaryFilterDefinitions.find(
    (d) => d.columnHeaderValue === secondaryFilterKey,
  );

  return definition?.displayName ?? secondaryFilterKey;
};

export const determineNonDefaultSecondaryFilterCount = ({
  stagingSecondaryFilters,
  myObservation,
  focusArea,
  feature,
  analysisName,
}: {
  stagingSecondaryFilters: SecondaryFilterSelection[];
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  myObservation: MyObservation | null;
  analysisName: string | undefined;
}): number => {
  let count = 0;

  for (const filter of stagingSecondaryFilters) {
    const defaultValue = determineDefaultValueForSecondaryFilter({
      secondaryFilterKey: filter.secondaryFilterKey,
      feature,
      focusArea,
      myObservation,
      analysisName,
    });

    if (defaultValue === null) {
      continue;
    }

    if (!isFilterStoreValueEqual(filter.value, defaultValue)) {
      count++;
    }
  }

  return count;
};

export const determineNonDefaultGlobalFilterCount = ({
  stagingGlobalFilters,
}: {
  stagingGlobalFilters: SecondaryFilterSelection[];
}): number => {
  let count = 0;

  for (const filter of stagingGlobalFilters) {
    const defaultValue: string[] = [];

    if (defaultValue === null) {
      continue;
    }

    if (!isFilterStoreValueEqual(filter.value, defaultValue)) {
      count++;
    }
  }

  return count;
};

export const determinePrimaryFilterSelections = ({
  primaryFilterKeys,
  feature,
  focusArea,
  analysisName,
  databaseId,
  databaseRevisionId,
  store,
  organizationId,
}: {
  primaryFilterKeys: PrimaryFilterKey[];
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  store: FilterStoreHook;
  organizationId: string;
  databaseId: string;
  databaseRevisionId: string;
}): PrimaryFilterSelection[] => {
  const values: PrimaryFilterSelection[] = [];

  for (const primaryFilterKey of primaryFilterKeys) {
    const value = getValueFromStoreByPrimaryFilterKey({
      store,
      feature,
      focusArea,
      analysisName,
      databaseRevisionId,
      databaseId,
      organizationId,
      primaryFilterKey,
    });

    if (value !== null) {
      values.push({ primaryFilterKey, value });
    }
  }

  return values;
};

export const arePrimaryFilterSelectionsEqual = (
  a: PrimaryFilterSelection[] | undefined,
  b: PrimaryFilterSelection[] | undefined,
): boolean => {
  if (a === undefined && b === undefined) {
    return true;
  }

  if (a === undefined || b === undefined) {
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (const selection of a) {
    const match = b.find(
      (s) =>
        s.primaryFilterKey === selection.primaryFilterKey &&
        isFilterStoreValueEqual(s.value, selection.value),
    );

    if (!match) {
      return false;
    }
  }

  return true;
};

export const areSecondaryFilterSelectionsEqual = (
  a: SecondaryFilterSelection[] | undefined,
  b: SecondaryFilterSelection[] | undefined,
): boolean => {
  if (a === undefined && b === undefined) {
    return true;
  }

  if (a === undefined || b === undefined) {
    return false;
  }

  if (a.length !== b.length) {
    return false;
  }

  for (const selection of a) {
    const match = b.find(
      (s) =>
        s.secondaryFilterKey === selection.secondaryFilterKey &&
        isFilterStoreValueEqual(s.value, selection.value),
    );

    if (!match) {
      return false;
    }
  }

  return true;
};

export const checkShouldApplyFilter = ({
  primaryFilterSelections,
  previousPrimaryFilterSelections,
  secondaryFilterSelections,
  previousSecondaryFilterSelections,
  shouldPreventAutoSubmit,
}: {
  feature: FiltersFeatureName;
  primaryFilterSelections: PrimaryFilterSelection[];
  previousPrimaryFilterSelections: PrimaryFilterSelection[] | undefined;
  secondaryFilterSelections: SecondaryFilterSelection[];
  previousSecondaryFilterSelections: SecondaryFilterSelection[] | undefined;
  shouldPreventAutoSubmit: boolean;
}) => {
  const arePrimaryFiltersInitialized = primaryFilterSelections.length > 0;
  const havePrimaryFiltersChanged = !arePrimaryFilterSelectionsEqual(
    previousPrimaryFilterSelections,
    primaryFilterSelections,
  );
  const areSecondaryFiltersInitialized = secondaryFilterSelections.length > 0;
  const haveSecondaryFiltersChanged = !areSecondaryFilterSelectionsEqual(
    previousSecondaryFilterSelections,
    secondaryFilterSelections,
  );

  const filtersReadyAndChanged =
    (arePrimaryFiltersInitialized || areSecondaryFiltersInitialized) &&
    (havePrimaryFiltersChanged || haveSecondaryFiltersChanged);

  return filtersReadyAndChanged && !shouldPreventAutoSubmit;
};

export const checkShouldApplyGlobalFilter = ({
  globalFilterSelections,
  previousGlobalFilterSelections,
  shouldPreventAutoSubmit,
}: {
  globalFilterSelections: SecondaryFilterSelection[];
  previousGlobalFilterSelections: SecondaryFilterSelection[] | undefined;
  shouldPreventAutoSubmit: boolean;
}) => {
  const areSecondaryFiltersInitialized = globalFilterSelections.length > 0;
  const haveSecondaryFiltersChanged = !areSecondaryFilterSelectionsEqual(
    previousGlobalFilterSelections,
    globalFilterSelections,
  );

  const filtersReadyAndChanged =
    areSecondaryFiltersInitialized && haveSecondaryFiltersChanged;

  return filtersReadyAndChanged && !shouldPreventAutoSubmit;
};

export const getFiltersFromSecondaryFilterSelections = ({
  secondaryFilterSelections,
}: {
  secondaryFilterSelections: SecondaryFilterSelection[];
}): FilterDefinitionForExtras[] => {
  return secondaryFilterSelections.map((selection) => {
    const inferred = inferTypeForFilterStore(selection.value);

    if (inferred.type === 'string[]') {
      return {
        columnHeaderValue: selection.secondaryFilterKey,
        values: inferred.stringArray,
      };
    }

    if (inferred.type === 'string') {
      return {
        columnHeaderValue: selection.secondaryFilterKey,
        values: [inferred.string],
      };
    }

    if (inferred.type === '[number, number]') {
      const filledValues: string[] = [];

      for (let i = inferred.numberRange[0]; i <= inferred.numberRange[1]; i++) {
        filledValues.push(i.toString());
      }
      return {
        columnHeaderValue: selection.secondaryFilterKey,
        values: filledValues,
      };
    }

    if (inferred.type === 'date') {
      return {
        columnHeaderValue: selection.secondaryFilterKey,
        values: [getDateAsYYYYMMDD(inferred.date)],
      };
    }

    if (inferred.type === 'dateRange') {
      return {
        columnHeaderValue: selection.secondaryFilterKey,
        values: [
          getDateAsYYYYMMDD(inferred.dateRange.startDate),
          getDateAsYYYYMMDD(inferred.dateRange.endDate),
          inferred.dateRange.dateRangeType,
        ],
      };
    }

    throw guardExhaustive(inferred);
  });
};

export interface EntitledDetailsInContextOfFiltersFeatureName {
  focusAreasWithMetrics: MyFocusAreaDefinition[];
  focusAreaMetrics: MyMetricDefinition[];
  focusAreaDefinition: MyFocusAreaDefinition | undefined;
}

export const getEntitledDetailsInContextOfFiltersFeatureName = ({
  feature,
  focusArea,
  myObservation,
}: {
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  myObservation: MyObservation | null;
}): EntitledDetailsInContextOfFiltersFeatureName => {
  const focusAreas = myObservation?.focusAreas ?? [];

  const focusAreasWithMetrics =
    focusAreas.filter((fa) => {
      return (
        fa.metrics.length > 0 &&
        isEntitledToFiltersFeatureNameInContextOfFocusArea({
          features: myObservation?.features ?? [],
          feature,
          focusArea: fa.focusArea,
        })
      );
    }) ?? [];

  const focusAreaMetrics =
    focusAreasWithMetrics.find((fa) => fa.focusArea === focusArea)?.metrics ??
    [];

  const focusAreaDefinition = focusAreasWithMetrics.find(
    (fA) => fA.focusArea === focusArea,
  );

  return {
    focusAreasWithMetrics,
    focusAreaMetrics,
    focusAreaDefinition,
  };
};

export const getSecondaryFilterSelectionsFromCustomVizFormat = ({
  feature,
  focusArea,
  focusAreas,
  filterSetDefinitionsWithValues,
  customVizFilters,
  dimensionDefinitions,
  analysisName,
  pipelineAnalysisConfiguration,
}: {
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  focusAreas: FocusAreaWithFilterSetNames[];
  filterSetDefinitionsWithValues: FilterSetDefinitionWithValues[];
  customVizFilters: FilterDefinitionForExtras[];
  dimensionDefinitions: DimensionDefinition[];
  analysisName: string | undefined;
  pipelineAnalysisConfiguration:
    | DynamicPipelineAnalysisConfiguration[]
    | undefined;
}): SecondaryFilterSelection[] => {
  const secondaryFilterDefinitions =
    determineSecondaryFilterDefinitionsForFeature({
      focusArea,
      feature,
      focusAreas,
      filterSetDefinitionsWithValues,
      analysisName,
      dimensionDefinitions,
      pipelineAnalysisConfiguration,
    });

  const selections: SecondaryFilterSelection[] = [];

  for (const filter of customVizFilters) {
    const definition = secondaryFilterDefinitions.find(
      (d) => d.columnHeaderValue === filter.columnHeaderValue,
    );

    if (!definition) {
      continue;
    }

    if (definition.displayType === 'range_scalar') {
      // find the smallest and the largest years from the values
      const minYear = Math.min(...filter.values.map((v) => parseInt(v, 10)));
      const maxYear = Math.max(...filter.values.map((v) => parseInt(v, 10)));

      selections.push({
        secondaryFilterKey: filter.columnHeaderValue,
        value: [minYear, maxYear],
      });
    } else if (definition.displayType === 'select_single') {
      selections.push({
        secondaryFilterKey: filter.columnHeaderValue,
        value: filter.values[0],
      });
    } else {
      selections.push({
        secondaryFilterKey: filter.columnHeaderValue,
        value: filter.values,
      });
    }
  }

  return selections;
};

export const inferTypeForFilterStore = (
  value: FilterStoreValue,
): FilterStoreTypeInference => {
  if (
    typeof value === 'object' &&
    value !== null &&
    'startDate' in value &&
    'endDate' in value &&
    'dateRangeType' in value
  ) {
    return {
      type: 'dateRange',
      dateRange: value,
    };
  }

  if (typeof value === 'string') {
    return {
      type: 'string',
      string: value,
    };
  }

  if (Array.isArray(value)) {
    if (isNumberRangeFilterStoreValue(value)) {
      return {
        type: '[number, number]',
        numberRange: value,
      };
    }

    return {
      type: 'string[]',
      stringArray: value,
    };
  }

  return {
    type: 'date',
    date: value,
  };
};

/**
 * Sanitize the filter store value before setting it.
 * This is to ensure that the value being set matches the expected type.
 * If the types do not match, an error will be thrown, unless the value can be converted.
 * @param value
 * @param selectionType
 */
export const sanitizeFilterStoreValueBeforeSetValue = ({
  value,
  selectionType,
}: {
  value: FilterStoreValue;
  selectionType: FiltersStateSelection['type'];
}) => {
  let valueSanitized = value;
  const inference = inferTypeForFilterStore(value);

  if (inference.type !== selectionType) {
    // Allow support for string[] to [number, number] conversion
    if (inference.type === 'string[]' && selectionType === '[number, number]') {
      // Get the smallest and largest values from the selection
      const minValue = Math.min(...inference.stringArray.map(Number));
      const maxValue = Math.max(...inference.stringArray.map(Number));

      valueSanitized = [minValue, maxValue];
    } else {
      throw new Error(
        `Invalid filter type: expected ${selectionType}, got ${inference.type}`,
      );
    }
  }

  return valueSanitized;
};

const isNumberRangeFilterStoreValue = (
  value: FilterStoreValue,
): value is [number, number] => {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    typeof value[0] === 'number' &&
    typeof value[1] === 'number'
  );
};

export type FilterStoreTypeInference =
  | {
      type: 'string';
      string: string;
    }
  | {
      type: 'string[]';
      stringArray: string[];
    }
  | {
      type: '[number, number]';
      numberRange: [number, number];
    }
  | {
      type: 'date';
      date: Date;
    }
  | {
      type: 'dateRange';
      dateRange: DateRangeFilter;
    };

export type GetFocusAreaResult = {
  focusArea: FocusArea;
  metric: string;
} | null;

export const getFocusArea = (
  primaryFilterValues: PrimaryFilterSelection[],
): GetFocusAreaResult => {
  const focusArea = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === 'focusArea',
  );

  if (!focusArea) {
    return null;
  }

  const infer = inferTypeForFilterStore(focusArea.value);

  if (infer.type !== 'string[]' || infer.stringArray.length !== 2) {
    return null;
  }
  return {
    focusArea: FocusArea.parse(infer.stringArray[0]),
    metric: infer.stringArray[1],
  };
};

export type GetMetricFromPrimaryFilterValuesResult = string | null;

export const getMetricFromPrimaryFilterValues = (
  primaryFilterValues: PrimaryFilterSelection[],
): GetMetricFromPrimaryFilterValuesResult => {
  return getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'metric',
    primaryFilterValues,
  });
};

export const getStringValueFromPrimaryFilterValues = ({
  primaryFilterKey,
  primaryFilterValues,
}: {
  primaryFilterKey: PrimaryFilterKey;
  primaryFilterValues: PrimaryFilterSelection[];
}): string | null => {
  const filter = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === primaryFilterKey,
  );

  if (!filter) {
    return null;
  }

  const infer = inferTypeForFilterStore(filter.value);

  if (infer.type !== 'string') {
    return null;
  }
  return infer.string;
};

export const getDateValueFromPrimaryFilterValues = ({
  primaryFilterKey,
  primaryFilterValues,
}: {
  primaryFilterKey: PrimaryFilterKey;
  primaryFilterValues: PrimaryFilterSelection[];
}): Date | null => {
  const filter = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === primaryFilterKey,
  );

  if (!filter) {
    return null;
  }

  const infer = inferTypeForFilterStore(filter.value);

  if (infer.type !== 'date') {
    return null;
  }
  return infer.date;
};

export const getListValueFromPrimaryFilterValues = ({
  primaryFilterKey,
  primaryFilterValues,
}: {
  primaryFilterKey: PrimaryFilterKey;
  primaryFilterValues: PrimaryFilterSelection[];
}): string[] | null => {
  const filter = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === primaryFilterKey,
  );

  if (!filter) {
    return null;
  }

  const infer = inferTypeForFilterStore(filter.value);

  if (infer.type !== 'string[]') {
    return null;
  }
  return infer.stringArray;
};

export const getViewByValueFromStore = ({
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
  store,
  focusArea,
  analysisName,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  focusArea: FocusArea | null;
  analysisName: string | undefined;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}): ViewByOption | null => {
  const rawValue = getSelectedStringValuePrimaryFilterFromStore({
    feature,
    organizationId,
    databaseRevisionId,
    databaseId,
    store,
    focusArea,
    analysisName,
    primaryFilterKey: 'viewBy',
  });

  const typedValue = ViewByOption.safeParse(rawValue);
  return typedValue.success ? typedValue.data : null;
};

export const getViewByValueFromPrimaryFilterValues = ({
  primaryFilterValues,
}: {
  primaryFilterValues: PrimaryFilterSelection[];
}): ViewByOption | null => {
  const filter = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === 'viewBy',
  );

  if (!filter) {
    return null;
  }

  const infer = inferTypeForFilterStore(filter.value);

  if (infer.type !== 'string') {
    return null;
  }

  const str = infer.string;

  const viewByValue = ViewByOption.safeParse(str);

  if (!viewByValue.success) {
    return null;
  }

  return viewByValue.data;
};

export const getBigTimeComparisonValueFromPrimaryFilterValues = ({
  primaryFilterValues,
}: {
  primaryFilterValues: PrimaryFilterSelection[];
}): BigTimePeriodComparison | null => {
  const filter = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === 'bigTimePeriodComparison',
  );

  if (!filter) {
    return null;
  }

  const infer = inferTypeForFilterStore(filter.value);

  if (infer.type !== 'string') {
    return null;
  }

  const str = infer.string;

  const bigTimeComparisonValue = BigTimePeriodComparison.safeParse(str);

  if (!bigTimeComparisonValue.success) {
    return null;
  }

  return bigTimeComparisonValue.data;
};

export const getDateRangeFromPrimaryFilterValues = ({
  primaryFilterValues,
}: {
  primaryFilterValues: PrimaryFilterSelection[];
}): DateRangeFilter | null => {
  const filter = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === 'dateRange',
  );

  if (!filter) {
    return null;
  }

  const infer = inferTypeForFilterStore(filter.value);

  if (infer.type !== 'dateRange') {
    return null;
  }

  return infer.dateRange;
};

export const getSelectedSecondaryBreakdownFromPrimaryFilterValues = ({
  primaryFilterValues,
}: {
  primaryFilterValues: PrimaryFilterSelection[];
}) => {
  const value = getStringValueFromPrimaryFilterValues({
    primaryFilterKey: 'secondaryBreakdown',
    primaryFilterValues,
  });

  if (value === SELECTED_SECONDARY_BREAKDOWN_NO_SELECTION_VALUE || !value) {
    return null;
  }

  return value;
};

export const getSelectedSingleDateFromStore = ({
  store,
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}) => {
  const value = getSelectedStringValuePrimaryFilterFromStore({
    feature,
    organizationId,
    databaseId,
    databaseRevisionId,
    store,
    primaryFilterKey: 'singleDate',
    // TODO: This is kind of hacky, we should probably pass these in like the rest of the utils.
    focusArea: null,
    analysisName: undefined,
  });

  if (value === EVENT_STUDY_SINGLE_DATE_DEFAULT_VALUE) {
    return null;
  }

  return value;
};

export const getSelectedEventStudyGroupFromStore = ({
  store,
  feature,
  organizationId,
  databaseRevisionId,
  databaseId,
}: {
  store: FilterStoreHook;
  feature: FiltersFeatureName;
  organizationId: string;
  databaseRevisionId: string;
  databaseId: string;
}) => {
  const storeValue = getValueFromStoreByPrimaryFilterKey({
    store,
    databaseId,
    databaseRevisionId,
    feature,
    organizationId,
    primaryFilterKey: 'eventStudyGroup',
    // TODO: This is kind of hacky, we should probably pass these in like the rest of the utils.
    focusArea: null,
    analysisName: undefined,
  });

  if (!storeValue) {
    return null;
  }

  const valueType = inferTypeForFilterStore(storeValue);

  if (valueType.type !== 'string[]') {
    return null;
  }

  if (valueType.stringArray.length < 2) {
    return null;
  }

  return {
    dimensionOrFilterName: valueType.stringArray[0],
    dimensionOrFilterValue: valueType.stringArray[1],
  };
};

export const getChartTypeFromPrimaryFilterValues = ({
  primaryFilterValues,
}: {
  primaryFilterValues: PrimaryFilterSelection[];
}): string | null => {
  const filter = primaryFilterValues.find(
    (filter) => filter.primaryFilterKey === 'chartType',
  );

  if (!filter) {
    return null;
  }

  const infer = inferTypeForFilterStore(filter.value);

  if (infer.type !== 'string') {
    return null;
  }

  return infer.string;
};

export const getFilterDisplayName = (
  columnHeaderValue: string,
  filterDefinitions: FilterSetDefinitionWithValues['filterDefinitions'],
) => {
  const filterDefinition = filterDefinitions.find(
    (definition) => definition.columnHeaderValue === columnHeaderValue,
  );

  return filterDefinition?.displayName ?? columnHeaderValue;
};

export const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatDateToDDMMYYYY = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const mapDateRangeToPreferredViewByOption = ({
  start,
  end,
}: DateRange): ViewByOption => {
  const minBucketInterval = 5;

  const diffInYears = differenceInYears(end, start);
  if (diffInYears >= minBucketInterval) {
    return 'year';
  }

  const diffInMonths = differenceInMonths(end, start);
  if (diffInMonths >= minBucketInterval) {
    return 'month';
  }

  const diffInWeeks = differenceInWeeks(end, start);
  if (diffInWeeks >= minBucketInterval) {
    return 'week';
  }

  return 'day';
};

export const customVizRequestHasBreakdown = (
  customVizRequest: CustomVizRequest & {
    inputs: {
      extras: {
        breakdownOption: string | undefined | null;
      };
    };
  },
) => {
  if (!customVizRequest?.inputs.extras.breakdownOption) {
    return false;
  }

  if (
    customVizRequest.inputs.extras.breakdownOption === GROUP_BY_DEFAULT_ITEM_KEY
  ) {
    return false;
  }

  return true;
};

/**
 * Returns the feature associated with a given custom viz ID, if applicable, or null otherwise.
 * @param customVizId - The custom viz ID to look up.
 * @returns The associated feature name, or null if not found.
 */
// eslint-disable-next-line complexity
export const getFeatureFromCustomVizId = ({
  customVizId,
}: {
  customVizId: CustomVizId;
}): FiltersFeatureName | null => {
  switch (customVizId) {
    case 'binScatterMultiSelect':
      return 'binScatterMultiSelect';
    case 'pipelineAnalysis':
      return 'employeeLifecycle';
    case 'heatmap':
      return 'exploreHeatmap';
    case 'biasAnalysis':
      return 'dataIntelligenceDeepDive';
    case 'successPhenotypeV2':
      return 'successFactors';
    case 'progress':
      return 'progressPage';
    case 'summaryStats':
      return 'summaryStats';
    case 'richText':
      return 'richText';
    case 'tableView':
      return 'tableView';
    case 'timeSeriesTrends':
      return 'timeSeriesTrendsPrimary';
    case 'geolocation':
      return 'geolocation';
    case 'externalStreamlit':
      return 'externalStreamlit';
    case 'binScatterCrossSelect':
    case 'hiringCopilot':
    case 'nlpGraph':
    case 'payBenchmarking':
    case 'payCorrection':
    case 'promotionCopilot':
    case 'topLevelAnalysis':
      return null;
    default:
      throw guardExhaustive(customVizId);
  }
};
